import {
  type PoiDetailRow,
  type PoiInfoFilters,
} from '../services/poi-info.service'
import { type CartoClient } from '../services/carto.helper'
import { fetchPoiInfo } from '../services/poi-info.service'
import { type CompareAggregationRow } from '../../campaigns/services/compare-aggregations.service'
import { toDate, formatDate } from 'app/utils/date'

export const AGE_KEYS = [
  {
    key: 'nvisitors_age_18_24',
    label: 'Between 18 and 24',
    short: '18 - 24',
  },
  {
    key: 'nvisitors_age_25_34',
    label: 'Between 25 and 34',
    short: '25 - 34',
  },
  {
    key: 'nvisitors_age_35_44',
    label: 'Between 35 and 44',
    short: '35 - 44',
  },
  {
    key: 'nvisitors_age_45_54',
    label: 'Between 45 and 54',
    short: '45 - 54',
  },
  {
    key: 'nvisitors_age_55_64',
    label: 'Between 55 and 64',
    short: '55 - 64',
  },
  {
    key: 'nvisitors_age_65_100',
    label: 'Between 65 and 100',
    short: '65 - 100',
  },
]

export const PROFILE_KEYS = [
  { key: 'nvisitors_visitor_profile_resident', label: 'Resident' },
  { key: 'nvisitors_visitor_profile_commuter', label: 'Commuter' },
  { key: 'nvisitors_visitor_profile_local', label: 'Local' },
  { key: 'nvisitors_visitor_profile_regional', label: 'Regional' },
  { key: 'nvisitors_visitor_profile_national', label: 'National' },
  { key: 'nvisitors_visitor_profile_international', label: 'International' },
  { key: 'nvisitors_visitor_profile_unknown', label: 'NA due to GDPR' },
]

const HOUR_PERIODS = ['Morning', 'Afternoon', 'Evening', 'Night']

const DAY_TYPES = ['Weekend', 'Weekday']

export function genderDataFromDetail(
  d: PoiDetailRow,
  selection: string[] = [],
) {
  const all = [
    {
      id: 'nvisitors_gender_m',
      category: 'Man',
      value: d.nvisitors_gender_m || 0,
    },
    {
      id: 'nvisitors_gender_f',
      category: 'Woman',
      value: d.nvisitors_gender_f || 0,
    },
  ]
  const items =
    selection.length > 0 ? all.filter((it) => selection.includes(it.id)) : all
  if (!items.length) return null
  const total = items.reduce((sum, it) => sum + it.value, 0)
  if (!total) return null
  return items.map((it) => ({
    ...it,
    perc: (it.value / total) * 100,
  }))
}

export function ageDataFromDetail(
  d: PoiDetailRow,
  useShortLabels = false,
  selection: string[] = [],
) {
  const keys =
    selection.length > 0
      ? AGE_KEYS.filter((a) => selection.includes(a.key))
      : AGE_KEYS
  if (!keys.length) return null
  const total = keys.reduce((sum, a) => sum + (d[a.key] || 0), 0)
  if (!total) return null
  return keys.map((a) => ({
    id: a.key,
    category: useShortLabels ? a.short : a.label,
    label: useShortLabels ? a.short : a.label,
    value: d[a.key] || 0,
    perc: ((d[a.key] || 0) / total) * 100,
  }))
}

export function nvisitorsFromColumns(d: PoiDetailRow, columns: string[]) {
  if (!columns.length) return d.nvisitors ?? 0
  return columns.reduce((sum, col) => sum + (d[col] || 0), 0)
}

export function visitorProfileFromDetail(
  d: PoiDetailRow,
  selection: string[] = [],
) {
  const keys =
    selection.length > 0
      ? PROFILE_KEYS.filter((p) => selection.includes(p.key))
      : PROFILE_KEYS
  if (!keys.length) return null
  const total = keys.reduce((sum, p) => sum + (d[p.key] || 0), 0)
  if (!total) return null
  return keys.map((p) => ({
    id: p.key,
    category: p.label,
    label: p.label,
    value: d[p.key] || 0,
    perc: ((d[p.key] || 0) / total) * 100,
  }))
}

export function buildTargetStatsFromQuery(
  ref: PoiDetailRow,
  comp: PoiDetailRow | null,
  isCompareMode: boolean,
) {
  const result: any = {}

  // In compare mode without compare data, skip demographics
  // (they'll be populated when compare data arrives via combineLatest)
  if (isCompareMode && !comp) return result

  const genderRef = genderDataFromDetail(ref)
  if (genderRef) {
    if (comp) {
      const genderComp = genderDataFromDetail(comp)
      result.gender = genderComp ? [genderRef, genderComp] : [genderRef]
    } else {
      result.gender = genderRef
    }
  }
  const ageRef = ageDataFromDetail(ref, true)
  if (ageRef) {
    if (comp) {
      const ageComp = ageDataFromDetail(comp, true)
      result.age = ageComp ? [ageRef, ageComp] : [ageRef]
    } else {
      result.age = ageRef
    }
  }
  const profileRef = visitorProfileFromDetail(ref)
  if (profileRef) {
    if (comp) {
      const profileComp = visitorProfileFromDetail(comp)
      result.visitor_profile = profileComp
        ? [profileRef, profileComp]
        : [profileRef]
    } else {
      result.visitor_profile = profileRef
    }
  }
  return result
}

export async function fetchAggregatedStats(
  cartoClient: CartoClient,
  baseFilters: PoiInfoFilters,
  poiId: string,
  dayFilter: string,
): Promise<any> {
  const [hourlyResults, habitsResults] = await Promise.all([
    Promise.all(
      HOUR_PERIODS.map((hour) =>
        fetchPoiInfo(cartoClient, {
          ...baseFilters,
          poi_id: poiId,
          filter_combined: `${dayFilter}-${hour}`,
        }),
      ),
    ),
    Promise.all(
      DAY_TYPES.flatMap((dayType) =>
        HOUR_PERIODS.map((hour) =>
          fetchPoiInfo(cartoClient, {
            ...baseFilters,
            poi_id: poiId,
            filter_combined: `${dayType}-${hour}`,
          }),
        ),
      ),
    ),
  ])

  const buildTarget = (
    rows: (PoiDetailRow | null)[],
    keys: { key: string; id: string }[],
    targetKey: string,
  ) => {
    const values = HOUR_PERIODS.map((hour, i) => {
      const d = rows[i]
      const items = keys.map((k) => ({
        id: k.id,
        key: targetKey,
        category: k.id,
        value: d?.[k.key] || 0,
        total: 0,
        perc: 0,
      }))
      const total = items.reduce((s, it) => s + it.value, 0)
      items.forEach((it) => {
        it.total = total
        it.perc = total ? (it.value / total) * 100 : 0
      })
      return { filter: hour, values: items }
    })
    return { values }
  }

  const buildHabitsTarget = () => {
    const values = HOUR_PERIODS.map((hour, hourIdx) => {
      const items = DAY_TYPES.map((dayType, dayIdx) => {
        const d = habitsResults[dayIdx * HOUR_PERIODS.length + hourIdx]
        const value = d?.nvisitors || 0
        return {
          id: `nvisitors_${dayType}`,
          key: 'habits',
          category: dayType,
          value,
          total: value,
          perc: 100,
        }
      })
      return { filter: hour, values: items }
    })
    return { values }
  }

  const ageKeys = AGE_KEYS.map((a) => ({ key: a.key, id: a.key }))
  const genderKeys = [
    { key: 'nvisitors_gender_m', id: 'nvisitors_gender_m' },
    { key: 'nvisitors_gender_f', id: 'nvisitors_gender_f' },
  ]

  return {
    age: buildTarget(hourlyResults, ageKeys, 'age'),
    gender: buildTarget(hourlyResults, genderKeys, 'gender'),
    habits: buildHabitsTarget(),
  }
}

export function mergeAggregatedStats(results: any[]) {
  if (results.length === 2) {
    const [ref, comp] = results
    const merged: any = {}
    for (const key of Object.keys(ref)) {
      merged[key] = [ref[key], comp[key]]
    }
    return merged
  }
  return results[0]
}

export function buildDetailAggs(d: PoiDetailRow) {
  return {
    avg: d.avgnvisitors,
    sum: d.sumnvisitors,
    min: d.minnvisitors,
    max: d.maxnvisitors,
    count: d.countnvisitors,
  }
}

export function buildCompareStats(
  ref: PoiDetailRow,
  comp: PoiDetailRow,
  existingTargetStats: any,
  isCompareMode: boolean,
) {
  return {
    visitors: [ref.nvisitors, comp.nvisitors],
    globalAggs: [buildDetailAggs(ref), buildDetailAggs(comp)],
    poiTraffic: [{ sum: ref.traffic }, { sum: comp.traffic }],
    globalTraffic: { avg: ref.avgtraffic },
    targetStats: {
      ...(existingTargetStats || {}),
      ...buildTargetStatsFromQuery(ref, comp, isCompareMode),
    },
  }
}

export function formatLegacyAggregatedData(
  data: any,
  filterField: string | null = null,
  newIndicatorKey: string | null = null,
) {
  const out = {}
  ;(data || []).forEach((value) => {
    if (!out[value.filter.hour]) {
      out[value.filter.hour] = {
        filter: value.filter.hour,
        values: [],
      }
    }

    let id = newIndicatorKey
      ? value.id.replace(value.key, newIndicatorKey)
      : value.id
    if (filterField) {
      const parts = id.split('_').slice(0, 2)
      parts.push(value.filter[filterField].toLowerCase())
      id = parts.join('_')
    }
    const previous = out[value.filter.hour].values.find((v) => v.id === id)
    if (!previous) {
      out[value.filter.hour].values.push({
        ...value,
        filter: value.filter.hour,
        id,
        key: newIndicatorKey || value.key,
        category: filterField ? value.filter[filterField] : value.category,
        label: filterField ? value.filter[filterField] : value.label,
      })
    } else {
      previous['value'] += value.value
      previous['total'] += value.value
      previous['perc'] = (previous['value'] * 100) / (previous['total'] || 1)
    }
  })
  const val = Object.values(out)

  val.forEach((row: any) => {
    const total = row.values.reduce((accum, i) => accum + i.value, 0)
    row.values.forEach((v) => {
      v.total = total
      v.perc = (v.value * 100) / (total || 1)
    })
  })
  return { values: val }
}

const TARGET_STATS_ORDER = [
  'no interest',
  'unused',
  'nousage',
  'low',
  'mid-low',
  'medium',
  'mid-high',
  'high',
]

const INTERESTED_KEYS = [
  'onlinebetplatforms_interested',
  'fooddelivery_interested',
  'transport_interested',
]

export function parseTargetStats(
  stats: any,
  delivery: any,
  isCompareMode: boolean,
) {
  const result = { ...stats }
  Object.keys(result).forEach((key) => {
    result[key] = result[key].map((r) => ({ ...r, unit: '%' }))
    const currentFilterKeys = Object.keys(
      delivery.subfilters.find((s) => s.id === key).values,
    )
    const rows = isCompareMode ? result[key] : [result[key]]
    rows.forEach((row) => {
      row.sort((a, b) => {
        a = a.length ? a[0] : a
        b = b.length ? b[0] : b
        let orderedA = TARGET_STATS_ORDER.indexOf(a.category.toLowerCase())
        if (orderedA === -1) {
          orderedA = currentFilterKeys.indexOf(a.id)
        }
        let orderedB = TARGET_STATS_ORDER.indexOf(b.category.toLowerCase())
        if (orderedB === -1) {
          orderedB = currentFilterKeys.indexOf(b.id)
        }
        return orderedA - orderedB
      })
    })
  })
  return result
}

export function buildCompareDeliveryStats(
  rows: CompareAggregationRow[],
  referenceDeliveryId: number,
  aggKey: 'sum' | 'avg',
) {
  const ref = rows.find((r) => r.delivery_id === referenceDeliveryId)
  const comp = rows.find((r) => r.delivery_id !== referenceDeliveryId)
  if (!ref || !comp) return null

  const refAgg = buildAggFromCompareRow(ref)
  const compAgg = buildAggFromCompareRow(comp)

  return {
    globalAggs: [refAgg, compAgg],
    visitors: [refAgg[aggKey], compAgg[aggKey]],
    poiTraffic: [{ sum: ref.traffic_sum }, { sum: comp.traffic_sum }],
    globalTraffic: { avg: ref.traffic_avg },
  }
}

export function buildAggFromCompareRow(row: CompareAggregationRow) {
  return {
    min: row.nvisitors_min,
    max: row.nvisitors_max,
    count: row.nvisitors_count,
    avg: row.nvisitors_avg,
    sum: row.nvisitors_sum,
  }
}

export function parsePoiRows<T extends { tags: any; properties: any }>(
  rows: T[],
): T[] {
  return rows.map((r) => ({
    ...r,
    tags: typeof r.tags === 'string' ? JSON.parse(r.tags) : r.tags,
    properties:
      typeof r.properties === 'string'
        ? JSON.parse(r.properties)
        : r.properties,
  }))
}

export function buildPoiListItems(
  pois: any[],
  brand: string,
  defaultRegionId = 'GR',
) {
  return pois.map((p) => ({
    label: p.name,
    value: p.id,
    region_id: p.region_id || defaultRegionId,
    brand,
    centroid: [p.lng, p.lat],
  }))
}

export function getColumnsFromTargetFilter(targetFilter: any): string[] {
  // Empty array = "entire population". The service layer converts [] to the
  // ['nvisitors'] sentinel before querying CARTO, and the SQL switches on
  // 'nvisitors' IN UNNEST(@columns) to read the precomputed total directly.
  if (!targetFilter) return []

  const activeSubfilter = targetFilter.subfilters.find((sf) => !!sf.value)
  if (activeSubfilter) {
    return Array.isArray(activeSubfilter.value)
      ? activeSubfilter.value
      : [activeSubfilter.value]
  }

  return []
}

export function isValidStat(val: number) {
  return !isNaN(val) && Number.isFinite(val) && val !== 0
}

export function safePercent(a: number, b: number): number | null {
  if (!b || !Number.isFinite(b)) return null
  const p = ((a - b) / b) * 100
  return isValidStat(p) ? p : null
}

export function createDeliveryLabel(delivery: {
  start: string
  finish: string
}) {
  const opts: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }
  const dayFrom = formatDate(toDate(delivery.start), opts)
  const dayTo = formatDate(toDate(delivery.finish), opts)
  return `${dayFrom} - ${dayTo}`
}

export function postProcessInterestedStats(
  targetStats: any,
  isCompareMode: boolean,
) {
  const result = { ...targetStats }
  INTERESTED_KEYS.forEach((k) => {
    if (result[k]) {
      result[k] = result[k].map((v) => {
        if (isCompareMode) {
          return v.map((o) => ({ ...o, value: o.perc }))
        } else {
          return { ...v, value: v.perc }
        }
      })
    }
  })
  return result
}
